/* GdprSelfService — drop-in profile section that lets the user
 * export everything we know about them (Article 15) or permanently
 * erase the account (Article 17). Soft-launch readiness.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { Download, Trash2, AlertTriangle, ShieldCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GdprSelfService() {
  const { language } = useLanguage();
  const bg = language === "bg";
  const [exporting, setExporting] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [confirmStr, setConfirmStr] = useState("");

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API}/me/gdpr/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nutramilo-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(bg ? "Експортирано успешно" : "Export downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при експорт" : "Export failed"));
    } finally {
      setExporting(false);
    }
  };

  const handleErase = async () => {
    const trigger = bg ? "ИЗТРИЙ" : "DELETE";
    if (confirmStr.trim().toUpperCase() !== trigger.toUpperCase()) {
      toast.error(
        bg
          ? `Напишете "${trigger}" в полето за да потвърдите`
          : `Type "${trigger}" in the box to confirm`,
      );
      return;
    }
    setErasing(true);
    try {
      const { data } = await axios.delete(`${API}/me/gdpr/account?confirm=DELETE`);
      toast.success(bg
        ? "Акаунтът ви е изтрит. Излизате от системата…"
        : "Your account has been deleted. Logging out…");
      // Wipe all local auth state
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch { /* noop */ }
      setTimeout(() => { window.location.href = "/"; }, 2200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при изтриване" : "Erase failed"));
      setErasing(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="gdpr-self-service">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-70">
        <ShieldCheck className="w-4 h-4 text-[#0EA5E9]" />
        {bg ? "Вашите GDPR права" : "Your GDPR rights"}
      </div>

      {/* Export */}
      <div className="p-4 rounded-2xl border border-gray-200 dark:border-white/10">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="font-semibold text-sm mb-1">
              {bg ? "Експорт на данни (Член 15)" : "Data export (Article 15)"}
            </div>
            <p className="text-sm opacity-70">
              {bg
                ? "Свалете всички данни, които съхраняваме за вас в JSON формат."
                : "Download every piece of data we hold about you as a JSON file."}
            </p>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            data-testid="gdpr-export-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0EA5E9] text-white text-sm font-medium hover:bg-[#0284C7] disabled:opacity-50"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {bg ? "Свали" : "Download"}
          </button>
        </div>
      </div>

      {/* Erase */}
      <div className="p-4 rounded-2xl border-2 border-red-500/30 bg-red-50/30 dark:bg-red-900/10">
        <div className="flex items-start gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-sm mb-1 text-red-700 dark:text-red-300">
              {bg ? "Изтриване на акаунт (Член 17)" : "Erase account (Article 17)"}
            </div>
            <p className="text-sm text-red-900/80 dark:text-red-200/80">
              {bg
                ? "Това ще изтрие необратимо всички ваши данни — оценки, лабораторни, дентални проверки, рентгенови снимки, AI разговори, abonament. Финансовите записи се анонимизират (по български закон). Email-ът ви става свободен за нова регистрация."
                : "This permanently erases all your data — assessments, labs, dental screenings, X-rays, AI conversations, subscription. Financial records are anonymised (Bulgarian law). Your email becomes available for re-registration."}
            </p>
          </div>
        </div>
        <input
          type="text"
          value={confirmStr}
          onChange={(e) => setConfirmStr(e.target.value)}
          placeholder={bg ? "Напишете ИЗТРИЙ за потвърждение" : "Type DELETE to confirm"}
          data-testid="gdpr-erase-confirm-input"
          className="w-full px-3 py-2 rounded-lg border border-red-500/30 bg-white/50 dark:bg-black/20 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40"
        />
        <button
          onClick={handleErase}
          disabled={erasing}
          data-testid="gdpr-erase-btn"
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
        >
          {erasing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          {bg ? "Изтрий акаунта ми завинаги" : "Erase my account permanently"}
        </button>
      </div>
    </div>
  );
}
